import { isEqual } from 'lodash';

import type { Entity } from '@swf/generated/analytics/entities';
import {
  sportsCompetition,
  sportsEvent,
  sportsMarket,
  sportsRace,
  sportsSelection,
  sportsSport
} from '@swf/generated/analytics/entities';
import type {
  MultipleAvailableOpportunity,
  SingleAvailableOpportunity
} from '@swf/generated/types/api/sports';
import type { PlaceBetResultRunner } from '@swf/sports/types/place-bet';
import getRunnerStatus from '@swf/sports/utils/getRunnerStatus';
import isOddsBoostMarket from '@swf/sports/utils/isOddsBoostMarket';

const createSingleEntities = (bettableOpportunities: SingleAvailableOpportunity[]) =>
  bettableOpportunities.flatMap((single) => {
    const selectionEntity = sportsSelection({
      selectionId: single.runnerId.selectionId,
      selectionOdds: single.winRunnerOdds?.decimalDisplayOdds?.decimalOdds,
      selectionStatus: getRunnerStatus(single.status),
      isStartingPrice: !single.winRunnerOdds
    });
    const marketEntity = sportsMarket({
      marketId: single.runnerId.marketId,
      marketInPlay: false,
      marketIsOddsBoost: isOddsBoostMarket(single.marketType)
    });
    const eventEntity = sportsEvent({
      eventId: Number(single.eventId),
      eventStartTime: new Date(single.eventStartTime).getTime()
    });
    const sportEntity = sportsSport({ sportId: Number(single.eventTypeId) });

    const enitities: Entity[] = [selectionEntity, marketEntity, eventEntity, sportEntity];

    if (single.competitionId) {
      const competitonEntity = sportsCompetition({ competitionId: single.competitionId });
      enitities.push(competitonEntity);
    }

    if (single.raceId) {
      const raceIdEntity = sportsRace({ raceId: single.raceId });
      enitities.push(raceIdEntity);
    }

    return enitities;
  });

const createMultipleEntities =
  (opportunities: SingleAvailableOpportunity[]) =>
  (bettableOpportunities: MultipleAvailableOpportunity[], resultRunners?: PlaceBetResultRunner[]) =>
    bettableOpportunities.flatMap((multiple) =>
      multiple.runnersIds.flatMap((runnerId) => {
        const opportunity = opportunities.find((opportunity) =>
          isEqual(opportunity.runnerId, runnerId)
        );
        const resultRunner =
          resultRunners &&
          resultRunners.find((resultRunner) => isEqual(resultRunner.runner, runnerId));

        if (!opportunity) {
          return [];
        }

        const selectionOdds = resultRunner ? resultRunner.winOdds : opportunity.winRunnerOdds;

        const trackedSelection = sportsSelection({
          selectionId: runnerId.selectionId,
          selectionOdds: selectionOdds?.decimalDisplayOdds?.decimalOdds,
          selectionStatus: getRunnerStatus(opportunity.status),
          isStartingPrice: !selectionOdds
        });
        const trackedEvent = sportsEvent({
          eventId: Number(opportunity.eventId),
          eventStartTime: new Date(opportunity.eventStartTime).getTime()
        });
        const trackedMarket = sportsMarket({
          marketId: runnerId.marketId,
          marketInPlay: false,
          marketIsOddsBoost: isOddsBoostMarket(opportunity.marketType)
        });
        const sportEntity = sportsSport({ sportId: Number(opportunity.eventTypeId) });

        const enitities: Entity[] = [trackedSelection, trackedEvent, trackedMarket, sportEntity];

        if (opportunity.competitionId) {
          const competitonEntity = sportsCompetition({ competitionId: opportunity.competitionId });
          enitities.push(competitonEntity);
        }

        if (opportunity.raceId) {
          const raceIdEntity = sportsRace({ raceId: opportunity.raceId });
          enitities.push(raceIdEntity);
        }

        return enitities;
      })
    );

export { createSingleEntities, createMultipleEntities };
